import { type FC, useRef } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import PhotoHeart from 'dibs-interiors-photos/exports/PhotoHeart';
import PhotoOverlayItem from 'dibs-interiors-photos/exports/PhotoOverlayItem';
import PhotoHeading from 'dibs-interiors-photos/exports/PhotoHeading';
import LazyLoad from 'dibs-react-lazy-image';
import { getSrcsetUrl, getSrcsetString } from 'dibs-interiors-photos/exports/srcSet';
import SocialDropdown from 'dibs-interiors-photos/exports/SocialDropdown';
import { trackPhotoClick, trackSavePhoto } from 'dibs-interiors-photos/exports/trackingHelpers';
import { type PhotoLike } from 'dibs-interiors-photos/exports/usePhotosLikeDataRefetch';

import { trackPhotoClick as trackGalleryOpened } from '../helpers/photosSearchBrowseTracking';
import { type PhotoItem_photo$data } from './__generated__/PhotoItem_photo.graphql';
import { getUrlFacetFilterFromUrl } from '../../shared/helpers/photosFacetFilterUrlPathHelper';
import { useServerVars } from '../../shared/ServerVarsContext';
import { PhotoItemQuickView } from './PhotoItemQuickView';

import styles from './PhotoItem.scss';

const LAZY_LOAD_OFFSET = 500;
const IMAGE_WIDTH = 400;
const IMG_SIZES = '(max-width: 568px) 50vw, (max-width: 768px) 33vw, 25vw';

type PhotoItemProps = {
    originalUrl: string;
    photo: PhotoItem_photo$data;
    onClick: (url: string) => void;
    onQuickViewOpen: () => void;
    onQuickViewClose: () => void;
    isQuickViewOpen: boolean;
    photoLikeData?: PhotoLike;
    isRefetchingPhotoLikes: boolean;
};

const messages = defineMessages({
    quickViewCta: {
        id: 'PhotosSearchBrowseContentPhotoItem.quickViewCta',
        defaultMessage: 'Show quick view information',
    },
});

export const PhotoItemComponent: FC<PhotoItemProps> = ({
    originalUrl,
    photo,
    onClick,
    onQuickViewClose,
    onQuickViewOpen,
    isQuickViewOpen,
    photoLikeData,
    isRefetchingPhotoLikes,
}) => {
    const intl = useIntl();
    const { isDesktop, isMobile } = useServerVars();
    const cardFrontRef = useRef<HTMLDivElement>(null);
    const firm = photo?.designFirm;
    const firmName = firm?.name || '';
    const projectName = firm?.designProjects?.[0]?.name || '';
    const roomName = photo?.room?.name || '';
    const photoStyles = photo?.styles || [];
    const photoStyle = photoStyles[0]?.name || '';
    const photoProjectType = photo?.project?.projectTypes?.[0] || '';
    const url = photo?.url || '';
    const photoUrl = photo?.path || '';
    const photoWidth = photo?.width || 0;
    const photoHeight = photo?.height || 0;
    const aspectRatio = (photoHeight * 100) / photoWidth;
    const { photoLikeId } = photoLikeData || {};
    const totalPhotoLikes = photo?.photoLike?.totalPhotoLikes;
    const locationHref = typeof window !== 'undefined' ? window.location.href : originalUrl;
    const { style = [], project = [], room = [] } = getUrlFacetFilterFromUrl(locationHref);

    const photoJoinedStyle = photoStyles.reduce((prev, cur) => {
        if (cur?.urlLabel && style.includes(cur.urlLabel)) {
            prev += ` ${cur?.name || ''}`;
        }
        return prev;
    }, '');

    //table of conditions https://1stdibs.atlassian.net/browse/CAM-720
    const showPhotoProjectAlt = project.length > 0 || (room.length > 0 && photoJoinedStyle);
    const photoProject = showPhotoProjectAlt ? photoProjectType : '';
    const hidePhotoStyleAlt = project.length > 0 && room.length === 0 && style.length === 0;
    const photoStyleAlt = hidePhotoStyleAlt ? '' : photoJoinedStyle || photoStyle;
    const altText = intl.formatMessage(
        {
            id: 'PhotosSearchBrowseContentPhotoItem.imageAlt',
            defaultMessage:
                '{photoStyleAlt} {photoProjectAlt}{roomName}. {projectName} by {firmName}.',
        },
        {
            photoStyleAlt,
            photoProjectAlt: photoProject ? `${photoProject} ` : '',
            roomName,
            projectName,
            firmName,
        }
    );

    const image = (
        <img
            className={styles.image}
            src={getSrcsetUrl(photoUrl, IMAGE_WIDTH)}
            srcSet={getSrcsetString(photoUrl)}
            sizes={IMG_SIZES}
            alt={altText}
        />
    );

    const handleLinkClick = (): void => {
        trackGalleryOpened();
        trackPhotoClick({ label: 'photos' });
        onClick(url);
    };

    return (
        <div className={styles.itemCard}>
            <div
                className={classnames({ [styles.hidden]: isQuickViewOpen })}
                // add tabindex to provide focus target when toggling between "front" (photo
                // tile) and "back" (quick view) of card
                tabIndex={-1}
                ref={cardFrontRef}
            >
                <div
                    className={styles.link}
                    tabIndex={-1}
                    role="button"
                    onClick={handleLinkClick}
                    onKeyDown={e => {
                        if (e.key === 'Spacebar' || e.key === 'Enter') {
                            handleLinkClick();
                        }
                    }}
                >
                    <div className={styles.imageWrapper} style={{ paddingTop: `${aspectRatio}%` }}>
                        <LazyLoad offsetVertical={LAZY_LOAD_OFFSET}>{image}</LazyLoad>
                        <noscript>{image}</noscript>
                    </div>
                    <div
                        className={classnames(styles.overlay, {
                            [styles.positionUnset]: !isDesktop,
                        })}
                    >
                        {!isMobile && (
                            <>
                                <PhotoOverlayItem>
                                    {!isRefetchingPhotoLikes && (
                                        <div className={styles.iconsWrapper}>
                                            <PhotoHeart
                                                photoNodeId={photo?.id}
                                                photoId={photo?.serviceId || ''}
                                                photoLikeId={photoLikeId}
                                                onPhotoLikeAdded={() =>
                                                    trackSavePhoto({ label: 'photos' })
                                                }
                                                totalPhotoLikes={totalPhotoLikes}
                                            />
                                        </div>
                                    )}
                                </PhotoOverlayItem>
                                <PhotoHeading
                                    photo={photo}
                                    handleLinkClick={handleLinkClick}
                                    selectedStyles={style}
                                    hasStyleFacet={!!style.length}
                                    hasRoomFacet={!!room.length}
                                    hasProjectTypeFacet={!!project.length}
                                />
                            </>
                        )}
                        <div className={styles.bottomWrapper}>
                            <PhotoOverlayItem>
                                <Link
                                    className={styles.quickViewCta}
                                    onClick={onQuickViewOpen}
                                    ariaLabel={intl.formatMessage(messages.quickViewCta)}
                                >
                                    <InfoCircle className={styles.quickViewIcon} />
                                </Link>
                            </PhotoOverlayItem>
                            {isMobile && (
                                <PhotoOverlayItem>
                                    <SocialDropdown
                                        photoNodeId={photo?.id}
                                        photoId={photo?.serviceId || ''}
                                        photoLikeId={photoLikeId}
                                        onPhotoLikeAdded={() => trackSavePhoto({ label: 'photos' })}
                                        photoUrl={photo?.url || ''}
                                    />
                                </PhotoOverlayItem>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <PhotoItemQuickView
                photo={photo}
                open={isQuickViewOpen}
                href={url}
                onClick={handleLinkClick}
                onClose={() => {
                    onQuickViewClose();
                    // ensure .hidden is removed before calling focus() by forcing callback to end
                    // of event loop (alternatively, remove .hidden using DOM methods here)
                    setTimeout(() => cardFrontRef.current?.focus());
                }}
            />
        </div>
    );
};

export const PhotoItem = createFragmentContainer(PhotoItemComponent, {
    photo: graphql`
        fragment PhotoItem_photo on PhotosSearchBrowsePhoto {
            ...PhotoItemQuickView_photo
            ...PhotoHeading_photo
            id
            serviceId
            url
            path
            width
            height
            styles {
                name
                urlLabel
            }
            room {
                name
            }
            project {
                projectTypes
            }
            designFirm {
                name
                designProjects {
                    name
                }
            }
            photoLike {
                totalPhotoLikes
            }
        }
    `,
});
